import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LearnerFeatureGates, PublicPlatformSettings } from '@examflow/api';
import { platform } from '../../lib/api';

const DEFAULT_GATES: LearnerFeatureGates = {
  mockExam: true,
  abilityReport: true,
  mistakeBook: true,
};

const DEFAULT_SETTINGS: PublicPlatformSettings = {
  siteName: 'ExamFlow',
  siteDescription: 'Exam practice platform',
  maintenanceMode: false,
  announcementEnabled: false,
  announcementMessage: '',
  announcementExpiresAt: null,
  supportEmail: '',
  supportDocsUrl: '',
  recommendedLanguageId: null,
  learnerGates: DEFAULT_GATES,
  registrationOwner: 'casdoor',
};

interface PlatformSettingsContextValue {
  settings: PublicPlatformSettings;
  isLoading: boolean;
  isError: boolean;
  learnerGates: LearnerFeatureGates;
  announcement: { enabled: boolean; message: string };
}

const PlatformSettingsContext = createContext<PlatformSettingsContextValue>({
  settings: DEFAULT_SETTINGS,
  isLoading: true,
  isError: false,
  learnerGates: DEFAULT_GATES,
  announcement: { enabled: false, message: '' },
});

export const platformSettingsKeys = {
  all: ['platform-settings'] as const,
  public: () => [...platformSettingsKeys.all, 'public'] as const,
};

function resolveAnnouncement(settings: PublicPlatformSettings): {
  enabled: boolean;
  message: string;
} {
  if (!settings.announcementEnabled || !settings.announcementMessage.trim()) {
    return { enabled: false, message: '' };
  }
  if (settings.announcementExpiresAt) {
    const expires = new Date(settings.announcementExpiresAt).getTime();
    if (!Number.isNaN(expires) && expires <= Date.now()) {
      return { enabled: false, message: '' };
    }
  }
  return { enabled: true, message: settings.announcementMessage.trim() };
}

export function PlatformSettingsProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: platformSettingsKeys.public(),
    queryFn: () => platform.getPublicSettings(),
    staleTime: 30_000,
    retry: 1,
  });

  const settings = query.data ?? DEFAULT_SETTINGS;
  const value: PlatformSettingsContextValue = {
    settings,
    isLoading: query.isLoading,
    isError: query.isError,
    learnerGates: settings.learnerGates ?? DEFAULT_GATES,
    announcement: resolveAnnouncement(settings),
  };

  return (
    <PlatformSettingsContext.Provider value={value}>{children}</PlatformSettingsContext.Provider>
  );
}

export function usePlatformSettings(): PlatformSettingsContextValue {
  return useContext(PlatformSettingsContext);
}
